import { CopyIcon, PlayIcon } from 'lucide-react';
import * as motion from 'motion/react-client';
import { type DOMAttributes, useCallback, useEffect, useState } from 'react';
import { codeToHtml } from 'shiki';

import { Button } from '@/components/ui/button';

interface Props {
  animationProps: {
    animate: {
      opacity: number;
    };
    initial: {
      opacity: number;
    };
  };
  task: {
    data: {
      code: string;
      instructions: string;
      language: string;
    };
    id: string;
    type: 'code-editor';
  };
}

const CodeViewer = ({ animationProps, task }: Props) => {
  const [html, setHtml] = useState<string>();

  useEffect(() => {
    void (async () => {
      setHtml(
        await codeToHtml(task.data.code, {
          lang: task.data.language,
          themes: {
            dark: 'min-dark',
            light: 'min-light'
          }
        })
      );
    })();
  }, [task.data.code, task.data.language]);

  const handleRunQueryButtonClick: DOMAttributes<HTMLButtonElement>['onClick'] =
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    useCallback(() => {}, []);

  const handleCopyButtonClick: DOMAttributes<HTMLDivElement>['onClick'] =
    useCallback(() => {
      void navigator.clipboard.writeText(task.data.code);
    }, [task.data.code]);

  return (
    <motion.div
      className="py-4"
      {...animationProps}
    >
      <div className="mb-4">{task.data.instructions}</div>
      <div className="flex gap-x-2">
        <div className="bg-outline mx-2 my-0.5 w-0.25" />
        <div className="dark:border-input-dark bg-accent dark:bg-input/50 grow overflow-hidden rounded-lg border shadow-xl">
          <div className="dark:border-b-input-dark bg-background dark:bg-input/30 flex items-center gap-x-2 border-b p-3">
            <div className="grow text-sm leading-none">
              {task.data.language}
            </div>
            <div
              className="text-muted-foreground flex items-center gap-x-1.5"
              onClick={handleCopyButtonClick}
            >
              <CopyIcon size={16} />
              <div className="text-xs font-medium">Copy</div>
            </div>
          </div>
          {html && (
            <div
              // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
          <div className="bg-background dark:bg-input/30 dark:border-t-input-dark border-t p-3">
            <Button
              onClick={handleRunQueryButtonClick}
              variant="brand"
            >
              <PlayIcon />
              Run Query
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CodeViewer;
